import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useReplicate } from './ReplicateContext';
import {
    updateScreenDisplayProperties,
    updateScreenUIElementMetadata,
    updateScreenUIElement,
    updateScreen,
} from './utils/state/screensSlice';
import { X, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

export const ReplicateToolDialog: React.FC = () => {
    const dispatch = useDispatch();
    const {
        showReplicateDialog,
        setShowReplicateDialog,
        selectedProperties,
        sourceScreenId,
        sourceElementId,
        targetScreens,
        setTargetScreens,
        targetElements,
        setTargetElements,
        clearSelection,
    } = useReplicate();

    const screens = useSelector((state: any) => state.screens.present.entities);

    if (!showReplicateDialog) return null;

    // Safety check for screens
    if (!screens || Object.keys(screens).length === 0) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-background rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-border">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-red-100 rounded-lg">
                                <X className="w-5 h-5 text-red-600" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-foreground">
                                    No Screens Available
                                </h2>
                                <p className="text-sm text-muted-foreground">
                                    Cannot proceed with replication
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => setShowReplicateDialog(false)}
                            className="p-2 hover:bg-muted rounded-lg transition-colors"
                        >
                            <X className="w-4 h-4 text-muted-foreground" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                        <p className="text-foreground mb-6">
                            No screens are available for replication. Please
                            create at least one screen before using the
                            replicate tool.
                        </p>

                        {/* Actions */}
                        <div className="flex justify-end">
                            <button
                                onClick={() => setShowReplicateDialog(false)}
                                className="px-4 py-2 text-sm font-medium text-foreground bg-muted rounded-lg hover:bg-muted transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const sourceScreen = sourceScreenId ? screens[sourceScreenId] : null;
    const sourceElement =
        sourceElementId && sourceScreen && sourceScreen.elements
            ? sourceScreen.elements.find((el: any) => el.id === sourceElementId)
            : null;

    const isReplicatingScreen = sourceScreenId && !sourceElementId;
    const isReplicatingElement = sourceElementId && sourceScreen;

    // Debug logging
    console.log('ReplicateToolDialog Debug:', {
        sourceScreenId,
        sourceElementId,
        sourceScreen: sourceScreen
            ? {
                  id: sourceScreen.id,
                  elementsCount: sourceScreen.elements?.length,
              }
            : null,
        sourceElement: sourceElement
            ? { id: sourceElement.id, type: sourceElement.type }
            : null,
        screensKeys: Object.keys(screens),
        isReplicatingScreen,
        isReplicatingElement,
    });

    // Safety check: ensure we have valid source data
    if (!sourceScreenId || (!isReplicatingScreen && !sourceElement)) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-background rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-border">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-red-100 rounded-lg">
                                <X className="w-5 h-5 text-red-600" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-foreground">
                                    Invalid Source
                                </h2>
                                <p className="text-sm text-muted-foreground">
                                    Cannot proceed with replication
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => setShowReplicateDialog(false)}
                            className="p-2 hover:bg-muted rounded-lg transition-colors"
                        >
                            <X className="w-4 h-4 text-muted-foreground" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                        <p className="text-foreground mb-6">
                            No valid source screen or element was selected for
                            replication. Please select a screen or element
                            first, then try again.
                        </p>

                        {/* Actions */}
                        <div className="flex justify-end">
                            <button
                                onClick={() => setShowReplicateDialog(false)}
                                className="px-4 py-2 text-sm font-medium text-foreground bg-muted rounded-lg hover:bg-muted transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Get all elements across all screens for element replication
    const allElements = Object.values(screens)
        .filter((screen: any) => screen && screen.elements)
        .flatMap((screen: any) =>
            screen.elements.map((element: any) => ({
                screenId: screen.id,
                screenName: screen.id,
                elementId: element.id,
                elementType: element.type,
                element,
            })),
        );

    const handleScreenToggle = (screenId: string) => {
        setTargetScreens((prev: string[]) =>
            prev.includes(screenId)
                ? prev.filter((id: string) => id !== screenId)
                : [...prev, screenId],
        );
    };

    const handleElementToggle = (elementKey: string) => {
        setTargetElements((prev: string[]) =>
            prev.includes(elementKey)
                ? prev.filter((key: string) => key !== elementKey)
                : [...prev, elementKey],
        );
    };

    const handleReplicate = () => {
        if (selectedProperties.length === 0) {
            toast.error('Please select at least one property to replicate.');
            return;
        }

        let replicatedCount = 0;

        if (isReplicatingScreen && sourceScreen) {
            // Replicate screen properties (display properties, stage properties, etc.)
            targetScreens.forEach((targetScreenId) => {
                const targetScreen = screens[targetScreenId];
                if (!targetScreen) return;

                let hasUpdates = false;
                const displayUpdates: any = {};
                const stageUpdates: any = {};

                selectedProperties.forEach((propertyPath) => {
                    if (propertyPath.startsWith('display.')) {
                        const displayProp = propertyPath.replace(
                            'display.',
                            '',
                        );
                        if (
                            sourceScreen.displayProperties[displayProp] !==
                            undefined
                        ) {
                            displayUpdates[displayProp] =
                                sourceScreen.displayProperties[displayProp];
                            hasUpdates = true;
                        }
                    } else if (propertyPath.startsWith('stage.')) {
                        const stageProp = propertyPath.replace('stage.', '');
                        if (stageProp === 'type') {
                            if (sourceScreen.stage.type !== undefined) {
                                stageUpdates.type = sourceScreen.stage.type;
                                hasUpdates = true;
                            }
                        } else {
                            // Handle stage metadata properties
                            if (
                                sourceScreen.stage.metadata &&
                                sourceScreen.stage.metadata[stageProp] !==
                                    undefined
                            ) {
                                if (!stageUpdates.metadata)
                                    stageUpdates.metadata = {};
                                stageUpdates.metadata[stageProp] =
                                    sourceScreen.stage.metadata[stageProp];
                                hasUpdates = true;
                            }
                        }
                    }
                });

                // Apply display property updates
                if (Object.keys(displayUpdates).length > 0) {
                    dispatch(
                        updateScreenDisplayProperties({
                            id: targetScreenId,
                            displayProperties: {
                                ...targetScreen.displayProperties,
                                ...displayUpdates,
                            },
                        }),
                    );
                }

                // Apply stage updates
                if (Object.keys(stageUpdates).length > 0) {
                    const newStage = { ...targetScreen.stage };

                    if (stageUpdates.type) {
                        newStage.type = stageUpdates.type;
                    }

                    if (stageUpdates.metadata) {
                        newStage.metadata = {
                            ...targetScreen.stage.metadata,
                            ...stageUpdates.metadata,
                        };
                    }

                    dispatch(
                        updateScreen({
                            id: targetScreenId,
                            data: { stage: newStage },
                        }),
                    );
                }

                if (hasUpdates) {
                    replicatedCount++;
                }
            });
        } else if (isReplicatingElement && sourceElement) {
            // Replicate element properties (both direct and metadata)
            targetElements.forEach((elementKey) => {
                const [targetScreenId, targetElementId] = elementKey.split(':');
                const targetScreen = screens[targetScreenId];
                if (!targetScreen) return;

                const targetElement = targetScreen.elements.find(
                    (el: any) => el.id === targetElementId,
                );
                if (!targetElement) return;

                // Properties that are stored directly on the element (not in metadata)
                const directElementProperties = [
                    'horizontalLocation',
                    'verticalLocation',
                    'groupLocation',
                    'order',
                    'layer',
                ];

                let hasUpdates = false;

                selectedProperties.forEach((property) => {
                    if (directElementProperties.includes(property)) {
                        // Handle direct element properties
                        if (sourceElement[property] !== undefined) {
                            console.log(
                                `Replicating direct property ${property}:`,
                                sourceElement[property],
                                'to element',
                                targetElementId,
                            );
                            dispatch(
                                updateScreenUIElement({
                                    screenId: targetScreenId,
                                    elementId: targetElementId,
                                    key: property,
                                    value: sourceElement[property],
                                }),
                            );
                            hasUpdates = true;
                        }
                    } else {
                        // Handle metadata properties
                        if (
                            sourceElement.metadata &&
                            sourceElement.metadata[property] !== undefined
                        ) {
                            console.log(
                                `Replicating metadata property ${property}:`,
                                sourceElement.metadata[property],
                                'to element',
                                targetElementId,
                            );
                            dispatch(
                                updateScreenUIElementMetadata({
                                    screenId: targetScreenId,
                                    elementId: targetElementId,
                                    key: property,
                                    value: sourceElement.metadata[property],
                                }),
                            );
                            hasUpdates = true;
                        }
                    }
                });

                if (hasUpdates) {
                    replicatedCount++;
                }
            });
        }

        if (replicatedCount > 0) {
            toast.success(
                `Successfully replicated ${
                    selectedProperties.length
                } properties to ${replicatedCount} ${
                    isReplicatingScreen ? 'screens' : 'elements'
                }.`,
            );
            setShowReplicateDialog(false);
            clearSelection();
        } else {
            toast.error(
                'No properties were replicated. Please check your selection.',
            );
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-background rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-border">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-orange-100 rounded-lg">
                            <Copy className="w-5 h-5 text-orange-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-foreground">
                                Replicate Properties
                            </h2>
                            <p className="text-sm text-muted-foreground">
                                Copy selected properties to other{' '}
                                {isReplicatingScreen ? 'screens' : 'elements'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => setShowReplicateDialog(false)}
                        className="p-2 hover:bg-muted rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5 text-muted-foreground" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {/* Source Info */}
                    <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <h3 className="font-medium text-blue-900 mb-2">
                            Source
                        </h3>
                        <p className="text-sm text-blue-700">
                            {isReplicatingScreen
                                ? `Screen: ${sourceScreen?.id}`
                                : `Element: ${sourceElement?.id} (${sourceElement?.type}) on ${sourceScreen?.id}`}
                        </p>
                        <p className="text-sm text-blue-600 mt-1">
                            Properties to copy: {selectedProperties.join(', ')}
                        </p>
                    </div>

                    {/* Target Selection */}
                    <div className="mb-6">
                        <h3 className="font-medium text-foreground mb-3">
                            Select Targets (
                            {isReplicatingScreen ? 'Screens' : 'Elements'})
                        </h3>

                        <div className="max-h-64 overflow-y-auto border border-border rounded-lg">
                            {isReplicatingScreen ? (
                                // Screen targets
                                <div className="divide-y divide-border">
                                    {Object.values(screens).map(
                                        (screen: any) => {
                                            if (screen.id === sourceScreenId)
                                                return null; // Don't show source screen

                                            return (
                                                <label
                                                    key={screen.id}
                                                    className="flex items-center p-3 hover:bg-muted/50 cursor-pointer"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={targetScreens.includes(
                                                            screen.id,
                                                        )}
                                                        onChange={() =>
                                                            handleScreenToggle(
                                                                screen.id,
                                                            )
                                                        }
                                                        className="w-4 h-4 text-orange-600 bg-muted border-border rounded focus:ring-orange-500"
                                                    />
                                                    <div className="ml-3">
                                                        <div className="text-sm font-medium text-foreground">
                                                            {screen.id}
                                                        </div>
                                                        <div className="text-xs text-muted-foreground">
                                                            {screen.stage?.type}{' '}
                                                            •{' '}
                                                            {screen.elements
                                                                ?.length ||
                                                                0}{' '}
                                                            elements
                                                        </div>
                                                    </div>
                                                </label>
                                            );
                                        },
                                    )}
                                </div>
                            ) : (
                                // Element targets
                                <div className="divide-y divide-border">
                                    {allElements.map(
                                        ({
                                            screenId,
                                            screenName,
                                            elementId,
                                            elementType,
                                            element,
                                        }) => {
                                            const elementKey = `${screenId}:${elementId}`;

                                            // Don't show source element
                                            if (
                                                screenId === sourceScreenId &&
                                                elementId === sourceElementId
                                            ) {
                                                return null;
                                            }

                                            // Only show elements of the same type
                                            if (
                                                elementType !==
                                                sourceElement?.type
                                            ) {
                                                return null;
                                            }

                                            return (
                                                <label
                                                    key={elementKey}
                                                    className="flex items-center p-3 hover:bg-muted/50 cursor-pointer"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={targetElements.includes(
                                                            elementKey,
                                                        )}
                                                        onChange={() =>
                                                            handleElementToggle(
                                                                elementKey,
                                                            )
                                                        }
                                                        className="w-4 h-4 text-orange-600 bg-muted border-border rounded focus:ring-orange-500"
                                                    />
                                                    <div className="ml-3">
                                                        <div className="text-sm font-medium text-foreground">
                                                            {elementId}
                                                        </div>
                                                        <div className="text-xs text-muted-foreground">
                                                            {elementType} on{' '}
                                                            {screenName}
                                                        </div>
                                                    </div>
                                                </label>
                                            );
                                        },
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-muted-foreground">
                            {isReplicatingScreen
                                ? `${targetScreens.length} screens selected`
                                : `${targetElements.length} elements selected`}
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowReplicateDialog(false)}
                                className="px-4 py-2 text-sm font-medium text-foreground bg-muted rounded-lg hover:bg-muted transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleReplicate}
                                disabled={
                                    selectedProperties.length === 0 ||
                                    (isReplicatingScreen
                                        ? targetScreens.length === 0
                                        : targetElements.length === 0)
                                }
                                className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                            >
                                <Check className="w-4 h-4" />
                                Replicate Properties
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
